// MCTS_v2 Unified Utilities - Clean, consolidated helper functions
// Max 300 lines - Single responsibility for game state operations

import {
  GameState,
  IMove,
  IPiece,
  ISquare,
  Position,
  PieceType,
  MoveType,
} from "../../types";
import { Piece } from "../../Piece";
import { GameStateManager } from "../../Managers/GameStateManager";
import { UUID } from "crypto";
import { MCTSMoveGenerationService } from "./MCTSMoveGenerationService";

// ============= CORE PIECE & POSITION UTILITIES =============

/**
 * Get a piece at a specific position - Primary method for position queries
 */
export function getPieceFromPosition(
  gameState: GameState,
  position: Position
): IPiece | null {
  // FIX: Add null-check for gameState.board
  if (
    !gameState?.board ||
    position.row < 0 ||
    position.row >= gameState.board.length ||
    position.col < 0 ||
    position.col >= gameState.board[position.row].length
  ) {
    return null;
  }

  // FIX: Default to empty square if null
  const square = gameState.board[position.row][position.col] ?? { Pieces: [] };
  return square?.Pieces?.[0] || null;
}

/**
 * Alternative method for board-only queries (used by MCTSUtils_v2 class)
 */
export function getPieceAt(
  board: ISquare[][],
  position: Position
): IPiece | null {
  // FIX: Add null-check for board
  if (
    !board ||
    position.row < 0 ||
    position.row >= board.length ||
    position.col < 0 ||
    position.col >= board[position.row].length
  ) {
    return null;
  }

  // FIX: Default to empty square if null
  const square = board[position.row][position.col] ?? { Pieces: [] };
  return square?.Pieces?.[0] || null;
}

/**
 * GameStateManager compatibility method
 */
export function getPieceAtWithManager(
  gameStateManager: GameStateManager,
  position: Position
): IPiece | null {
  const pieces = gameStateManager.getPiecesAt(position);
  return pieces.length > 0 ? pieces[0] : null;
}

// ============= KING FINDING UTILITIES =============

/**
 * Consolidated king finding function - supports both our king and enemy king
 */
export function findKing(
  gameState: GameState,
  isLight: boolean,
  findEnemy: boolean = false
): Position | null {
  const targetIsLight = findEnemy ? !isLight : isLight;

  // FIX: Add null-check for gameState.board
  if (!gameState?.board) return null;

  for (let row = 0; row < gameState.board.length; row++) {
    for (let col = 0; col < gameState.board[row].length; col++) {
      // FIX: Default to empty square if null
      const square = gameState.board[row][col] ?? { Pieces: [] };
      for (const piece of square.Pieces) {
        if (piece.isKing && piece.isLight === targetIsLight) {
          return { row, col };
        }
      }
    }
  }
  return null;
}

// ============= GAME STATE CLONING =============

/**
 * Create a clone of a piece with proper prototype methods
 */
export function clonePieceWithPrototype(originalPiece: IPiece): IPiece {
  const reconstructedPiece = new Piece(
    originalPiece.type,
    originalPiece.isLight,
    originalPiece.direction
  );

  // Store the properly initialized movementStrategy
  const properMovementStrategy = reconstructedPiece.movementStrategy;

  // Copy all properties from the original piece
  Object.assign(reconstructedPiece, {
    ...originalPiece,
  });

  // Restore the proper movementStrategy
  reconstructedPiece.movementStrategy = properMovementStrategy;
  return reconstructedPiece;
}

/**
 * Legacy clone method for backward compatibility
 * FIX: Deprecated - use clonePieceWithPrototype for full functionality
 */
export function clonePiece(originalPiece: IPiece): IPiece {
  const piece = new Piece(originalPiece.type, originalPiece.isLight);

  // Copy all piece properties
  piece.isKing = originalPiece.isKing;
  piece.poisonedStatus = { ...originalPiece.poisonedStatus };
  piece.zombifiedStatus = originalPiece.zombifiedStatus;
  piece.isFainted = originalPiece.isFainted;
  piece.isEntangled = originalPiece.isEntangled;
  piece.isShadowed = originalPiece.isShadowed;
  piece.isInfected = originalPiece.isInfected;
  piece.direction = originalPiece.direction;
  piece.hasCharged = originalPiece.hasCharged;
  piece.hasMoved = originalPiece.hasMoved;
  piece.hasShed = originalPiece.hasShed;
  piece.hasLilyJumped = originalPiece.hasLilyJumped;
  piece.isMeadowed = originalPiece.isMeadowed;
  piece.isRiding = originalPiece.isRiding;
  piece.isHousing = originalPiece.isHousing;
  piece.isLone = originalPiece.isLone;
  piece.isDormant = originalPiece.isDormant;

  return piece;
}

/**
 * Creates a complete deep clone of a game state
 * FIX: Handle null/undefined squares by defaulting to empty { Pieces: [] }
 * FIX: Switch to clonePieceWithPrototype for proper movement strategy preservation
 * FIX: Add try-catch for robustness during cloning
 */
export function cloneFullGameState(state: GameState): GameState {
  try {
    const clonedBoard: ISquare[][] = [];

    for (let row = 0; row < (state.board?.length ?? 8); row++) {
      clonedBoard[row] = [];
      for (let col = 0; col < (state.board?.[row]?.length ?? 8); col++) {
        // FIX: Default to empty square if null/undefined
        const originalSquare = state.board?.[row]?.[col] ?? { Pieces: [] };
        const clonedSquare: ISquare = { Pieces: [] };

        for (const originalPiece of originalSquare.Pieces ?? []) {
          // FIX: Use prototype-aware clone
          const clonedPiece = clonePieceWithPrototype(originalPiece);
          clonedSquare.Pieces.push(clonedPiece);
        }

        clonedBoard[row][col] = clonedSquare;
      }
    }

    return {
      mode: state.mode,
      board: clonedBoard,
      state: state.state,
      lightKing: state.lightKing,
      darkKing: state.darkKing,
      lightPlayer: state.lightPlayer,
      darkPlayer: state.darkPlayer,
      isLightTurn: state.isLightTurn,
      currentPlayer: state.currentPlayer,
      turnCount: state.turnCount,
      lightWon: state.lightWon,
      skipTurnChange: state.skipTurnChange,
      fenHistory: state.fenHistory,
      activeBonusTurnPieceId: state.activeBonusTurnPieceId,
      zombieRacePieceType: state.zombieRacePieceType,
    };
  } catch (error) {
    // FIX: Error handling - log and return partial state or throw
    throw new Error("Failed to clone game state - invalid board structure");
  }
}

// ============= MOVE UTILITIES =============

/**
 * Convert position and move to command format
 */
export function moveToCommand(
  fromPosition: Position | null,
  move: IMove | null
): { fromPosition: Position; move: IMove } | null {
  if (!fromPosition || !move) return null;
  return { fromPosition, move };
}

/**
 * Compare two moves for equality
 */
export function areMovesEqual(
  moveA: { fromPosition: Position; move: IMove } | null | undefined,
  moveB: { fromPosition: Position; move: IMove } | null | undefined
): boolean {
  if (!moveA || !moveB) return moveA === moveB;
  return (
    moveA.fromPosition.row === moveB.fromPosition.row &&
    moveA.fromPosition.col === moveB.fromPosition.col &&
    moveA.move.position.row === moveB.move.position.row &&
    moveA.move.position.col === moveB.move.position.col &&
    moveA.move.moveType === moveB.move.moveType
  );
}

// ============= GAME MECHANICS UTILITIES =============

/**
 * Check if there's an enemy fungi piece adjacent to or on the given position
 * FIX: Add bounds/null checks, check for enemy fungi specifically
 */
export function hasEnemyFungiNearby(
  state: GameState,
  position: Position,
  treeIsLight: boolean
): boolean {
  if (!state?.board) return false;

  // First check same square
  const square = state.board[position.row]?.[position.col];
  if (square?.Pieces.some(p => p.type === PieceType.Fungi && p.isLight !== treeIsLight)) {
    return true;
  }

  // Then check adjacent squares
  const directions = [
    { row: -1, col: -1 },
    { row: -1, col: 0 },
    { row: -1, col: 1 },
    { row: 0, col: -1 },
    { row: 0, col: 1 },
    { row: 1, col: -1 },
    { row: 1, col: 0 },
    { row: 1, col: 1 },
  ];

  for (const dir of directions) {
    const adjRow = position.row + dir.row;
    const adjCol = position.col + dir.col;

    if (
      adjRow >= 0 &&
      adjRow < state.board.length &&
      adjCol >= 0 &&
      adjCol < state.board[adjRow].length
    ) {
      // FIX: Default to empty if null
      const adjSquare = state.board[adjRow][adjCol] ?? { Pieces: [] };
      if (adjSquare.Pieces.some((p) => p.type === PieceType.Fungi && p.isLight !== treeIsLight)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Validate and update meadow effects in the game state
 * Sets isMeadowed based on whether an enemy Fungi is on the same square or adjacent.
 */
export function validateMeadowEffects(state: GameState): void {
  if (!state?.board) return;

  for (let row = 0; row < state.board.length; row++) {
    for (let col = 0; col < state.board[row].length; col++) {
      // FIX: Skip if square null
      const square = state.board[row][col] ?? { Pieces: [] };
      const tree = square.Pieces.find((p) => p.type === PieceType.Tree);

      if (tree) {
        // Set meadow status based on enemy fungi presence (same square or adjacent)
        tree.isMeadowed = hasEnemyFungiNearby(state, { row, col }, tree.isLight);
      }
    }
  }
}

/**
 * Fix moves involving pieces capturing or moving into trees
 * FIX: Add null-check for board access
 */
export function fixTreeCaptures(moves: IMove[], board: ISquare[][]): IMove[] {
  return moves.map((move) => {
    if (move.toPiece?.type !== PieceType.Tree) return move;

    // FIX: Bounds/null check
    if (
      !board ||
      move.position.row < 0 ||
      move.position.row >= board.length ||
      move.position.col < 0 ||
      move.position.col >= board[move.position.row].length
    ) {
      return move;
    }

    // FIX: Default to empty if null
    const targetSquare = board[move.position.row][move.position.col] ?? {
      Pieces: [],
    };
    if (targetSquare.Pieces.length > 1) {
      const nonTreePieces = targetSquare.Pieces.filter(
        (p) => p.type !== PieceType.Tree
      );

      if (nonTreePieces.length > 0) {
        const housedPiece = nonTreePieces[0];
        const isMovingLight = !move.toPiece.isLight;

        if (housedPiece.isLight !== isMovingLight) {
          return { ...move, moveType: MoveType.capture, toPiece: housedPiece };
        } else if (move.moveType === MoveType.capture) {
          return {
            ...move,
            moveType: MoveType.house,
            toPiece:
              targetSquare.Pieces.find((p) => p.type === PieceType.Tree) ||
              null,
          };
        }
      }
    }
    return move;
  });
}

/**
 * Apply a move to a game state with snake respawn handling (for simulations)
 * FIX: Implement actual Snake respawn on capture
 * FIX: Add null-checks and full capture/respawn logic
 * FIX: Handle side effects more robustly
 */
export function applyMoveWithSnakeRespawn(
  state: GameState,
  fromPosition: Position,
  move: IMove,
  piece: IPiece
): void {
  try {
    // FIX: Ensure squares exist, default if null
    const fromSquare = (state.board[fromPosition.row][fromPosition.col] ??= {
      Pieces: [],
    });
    const toSquare = (state.board[move.position.row][move.position.col] ??= {
      Pieces: [],
    });

    // Remove piece from original position
    fromSquare.Pieces = fromSquare.Pieces.filter((p) => p.id !== piece.id);

    // Handle captures (remove captured pieces) and check for Snake
    let capturedPiece: IPiece | null = null;
    if (move.toPiece) {
      capturedPiece =
        toSquare.Pieces.find((p) => p.id === move.toPiece!.id) ?? null;
      if (capturedPiece) {
        toSquare.Pieces = toSquare.Pieces.filter(
          (p) => p.id !== capturedPiece!.id
        );

        // FIX: Implement Snake respawn if captured
        if (capturedPiece.type === PieceType.Snake && !capturedPiece.hasShed) {
          // Respawn at home row (Light: row 7? Wait, per rules: Light home row 0, Dark 7? Adjust per your docs)
          // Assuming Light respawns row 0, Dark row 7
          const respawnRow = capturedPiece.isLight ? 0 : 7; // Confirm with rules
          const respawnCol = 0; // Or find empty home square
          const respawnSquare = (state.board[respawnRow][respawnCol] ??= {
            Pieces: [],
          });

          const respawnedSnake = clonePieceWithPrototype(capturedPiece);
          respawnedSnake.hasShed = true; // Or per rules
          respawnSquare.Pieces.push(respawnedSnake);
        }
      }
    }

    // Place piece at new position
    toSquare.Pieces.push(piece);

    // Handle side effects (expanded for simulation accuracy)
    if (move.sideEffects?.length > 0) {
      for (const sideEffect of move.sideEffects) {
        if (sideEffect.piecesToEffect) {
          // Apply effects (e.g., poison, zombie) - add logic as needed
          for (const targetPiece of sideEffect.piecesToEffect) {
            // Example: if (sideEffect.type === 'zombify') targetPiece.zombifiedStatus = true;
          }
        }
      }
    }

    // Toggle turn
    state.isLightTurn = !state.isLightTurn;
  } catch (error) {
    // FIX: Error handling
    throw new Error("Failed to apply move - invalid state");
  }
}

/**
 * Safely switch turn in a game state
 * @param state The game state to modify
 * @returns The modified state (for chaining)
 */
export function switchTurnSafely(state: GameState): GameState {
  if (!state) {
    throw new Error("Invalid game state for turn switch");
  }
  state.isLightTurn = !state.isLightTurn;
  return state;
}

/**
 * Standardized move simulation function
 * Simulates applying a move to a cloned state and returns the new state
 * @param originalState The original game state
 * @param moveInfo The move to simulate
 * @returns Cloned state after the move, or null on failure
 */
export function simulateMove(
  originalState: GameState,
  moveInfo: { fromPosition: Position; move: IMove }
): GameState | null {
  try {
    const clonedState = cloneFullGameState(originalState);
    const piece = getPieceFromPosition(clonedState, moveInfo.fromPosition);

    if (!piece) {
      return null;
    }

    applyMoveWithSnakeRespawn(
      clonedState,
      moveInfo.fromPosition,
      moveInfo.move,
      piece
    );

    validateMeadowEffects(clonedState);

    return clonedState;
  } catch (error) {
    return null;
  }
}

/**
 * Check if a position is valid on the board
 * @param position Position to check
 * @param board Board reference (optional, defaults to 8x8)
 * @returns True if position is within bounds
 */
export function isValidPosition(
  position: Position,
  board?: ISquare[][]
): boolean {
  const rows = board?.length ?? 8;
  const cols = board?.[0]?.length ?? 8;
  return (
    position.row >= 0 &&
    position.row < rows &&
    position.col >= 0 &&
    position.col < cols
  );
}

// ============= LEGACY COMPATIBILITY CLASS =============

/**
 * MCTSUtils_v2 class for backward compatibility with existing priority imports
 */
export class MCTSUtils_v2 {
  static getPieceAt(board: ISquare[][], position: Position): IPiece | null {
    return getPieceAt(board, position);
  }
}

// ============= OPTIONAL TEST FUNCTION (Comment out in production) =============
/*
function testCloning() {
  const mockState: GameState = { board: Array(8).fill(Array(8).fill({ Pieces: [] })) };
  mockState.board[0][5] = null; // Simulate corruption
  const cloned = cloneFullGameState(mockState);
 // Should be { Pieces: [] }
  // Add more tests...
}
// testCloning();
*/
